/**
 * Operator-facing copy for the canonical time-range validation codes.
 *
 * Ported from `feat/alerts-list-querying`. The point of naming each code is that
 * an out-of-range period must never render as an ordinary empty chart — "no
 * data" and "you asked for something we cannot answer" look identical on a chart
 * and mean completely different things.
 */
import i18n from "@/shared/lib/i18n/index.ts";
import { formatDate } from "@/shared/lib/i18n/formatters.ts";
import { toAlertApiError } from "./envelope";
import { ANALYTICS_MAX_LOOKBACK_DAYS } from "@/alerts/types/alerts-analytics";

export type AlertTimeRangeErrorCode =
  | "time_range_required"
  | "conflicting_time_range_filters"
  | "incomplete_time_range"
  | "invalid_time_range_order"
  | "timezone_required"
  | "validation_error"
  | "time_range_out_of_bounds"
  | "future_time_range_not_allowed"
  | "location_timezone_not_configured";

/**
 * The backend's boundary is a UTC instant but the operator picks a calendar day,
 * so the Y/M/D are read in UTC and re-anchored locally before formatting —
 * formatting the instant directly would shift the day for a viewer west of UTC.
 */
const asDate = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return null;
  const utc = new Date(ms);
  return formatDate(new Date(utc.getUTCFullYear(), utc.getUTCMonth(), utc.getUTCDate()));
};

/** True when the failure is about the requested PERIOD, not the data. */
export const isAlertTimeRangeError = (error: unknown): boolean => {
  const code = toAlertApiError(error).code;
  return code != null && code !== "";
};

/** The location has no timezone, so a local day cannot be resolved at all. */
export const isLocationTimezoneMissing = (error: unknown): boolean =>
  toAlertApiError(error).code === "location_timezone_not_configured";

export const alertTimeRangeErrorMessage = (error: unknown): string => {
  const normalized = toAlertApiError(error);
  const payload = normalized.payload ?? {};
  const code = normalized.code as AlertTimeRangeErrorCode | null;

  switch (code) {
    case "time_range_required":
      return i18n.t("errors.timeRange.required", { ns: "alerts" });
    case "conflicting_time_range_filters":
      return i18n.t("errors.timeRange.conflicting", { ns: "alerts" });
    case "incomplete_time_range":
      return i18n.t("errors.timeRange.incomplete", { ns: "alerts" });
    case "invalid_time_range_order":
      return i18n.t("errors.timeRange.invalidOrder", { ns: "alerts" });
    case "timezone_required":
      return i18n.t("errors.timeRange.timezoneRequired", { ns: "alerts" });
    case "future_time_range_not_allowed":
      return i18n.t("errors.timeRange.futureNotAllowed", { ns: "alerts" });
    case "location_timezone_not_configured":
      return i18n.t("errors.timeRange.timezoneNotConfigured", { ns: "alerts" });
    case "time_range_out_of_bounds": {
      const days =
        typeof payload.max_days === "number" ? payload.max_days : ANALYTICS_MAX_LOOKBACK_DAYS;
      if (payload.constraint === "max_lookback") {
        const earliest = asDate(payload.earliest_start_time);
        return earliest
          ? i18n.t("errors.timeRange.maxLookbackFrom", { ns: "alerts", days, earliest })
          : i18n.t("errors.timeRange.maxLookback", { ns: "alerts", days });
      }
      return i18n.t("errors.timeRange.tooLong", { ns: "alerts", days });
    }
    default:
      return normalized.message || i18n.t("errors.timeRange.generic", { ns: "alerts" });
  }
};
